"use client";

import { useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import { FileDown, Package } from "lucide-react";
import { cn } from "@/shared/utils/cn";
import styles from "./download-page-layout.module.css";
import {
  DOWNLOAD_RESOURCE_PANEL_ID,
  scrollDownloadResourcePanelToTop,
} from "./download-scroll-utils";
import {
  DownloadLinksDialog,
  EmptyState,
  ResourcePostCard,
  type DownloadDialogState,
} from "./download-resource-card";
import type {
  DownloadLink,
  DownloadProduct,
  DownloadResourceCategory,
  DownloadResourcePost,
} from "./download-types";

type DownloadResourcePanelProps = {
  categories: DownloadResourceCategory[];
  currentProduct?: DownloadProduct;
  downloadAction: string;
  downloadDialogClose: string;
  downloadLinkLabels: Record<string, string>;
  emptyResources: string;
  missingProduct: string;
  resourceCategoryAll: string;
};

type MobileCategorySelection = {
  categoryId: number | null;
  productId?: number;
};

type DesktopCategorySelection = {
  categoryId: number | null;
  productId?: number;
};

/** 渲染下载资源内容区；移动端使用分类 Tab，桌面端展示全部资源分组。 */
export function DownloadResourcePanel(props: DownloadResourcePanelProps) {
  const state = useDownloadResourcePanelState(
    props.categories,
    props.currentProduct?.id,
  );

  if (!props.currentProduct) {
    return <MissingProductPanel message={props.missingProduct} />;
  }

  return (
    <ResourcePanelSection
      {...props}
      currentProduct={props.currentProduct}
      state={state}
    />
  );
}

type ResourcePanelSectionProps = Omit<
  DownloadResourcePanelProps,
  "currentProduct"
> & {
  currentProduct: DownloadProduct;
  state: DownloadResourcePanelState;
};

function ResourcePanelSection({
  categories,
  currentProduct,
  downloadAction,
  downloadDialogClose,
  downloadLinkLabels,
  emptyResources,
  resourceCategoryAll,
  state,
}: ResourcePanelSectionProps) {
  const activeCategory = getActiveResourceCategory(
    categories,
    state.activeCategoryId,
  );
  const desktopCategories = getDesktopResourceCategories(
    categories,
    state.activeDesktopCategoryId,
  );
  const openDownloadDialog = (
    post: DownloadResourcePost,
    links: DownloadLink[],
  ) => state.setDownloadDialog({ links, title: post.post_title });

  return (
    <section
      className={cn(
        "relative left-px w-full max-w-full min-w-0 overflow-hidden scroll-mt-[104px] lg:left-0 lg:scroll-mt-20",
        styles.downloadPageResourcePanel,
      )}
      id={DOWNLOAD_RESOURCE_PANEL_ID}
    >
      <ResourcePanelStickyHeader
        activeCategoryId={state.activeDesktopCategoryId}
        allLabel={resourceCategoryAll}
        categories={categories}
        currentProductName={currentProduct.name}
        onSelect={state.setActiveDesktopCategoryId}
      />
      <ResourcePanelLists
        activeCategory={activeCategory}
        categories={categories}
        desktopCategories={desktopCategories}
        downloadAction={downloadAction}
        downloadLinkLabels={downloadLinkLabels}
        emptyResources={emptyResources}
        onDownload={openDownloadDialog}
        state={state}
        currentProductName={currentProduct.name}
      />
      <ResourceDownloadDialog closeLabel={downloadDialogClose} state={state} />
    </section>
  );
}

/** 渲染当前资源的下载地址选择弹窗。 */
function ResourceDownloadDialog({
  closeLabel,
  state,
}: {
  closeLabel: string;
  state: DownloadResourcePanelState;
}) {
  if (!state.downloadDialog) return null;
  return (
    <DownloadLinksDialog
      closeLabel={closeLabel}
      links={state.downloadDialog.links}
      onClose={() => state.setDownloadDialog(null)}
      title={state.downloadDialog.title}
    />
  );
}

type DownloadResourcePanelState = ReturnType<
  typeof useDownloadResourcePanelState
>;

type ResourcePanelListsProps = {
  activeCategory?: DownloadResourceCategory;
  categories: DownloadResourceCategory[];
  currentProductName: string;
  desktopCategories: DownloadResourceCategory[];
  downloadAction: string;
  downloadLinkLabels: Record<string, string>;
  emptyResources: string;
  onDownload: (post: DownloadResourcePost, links: DownloadLink[]) => void;
  state: DownloadResourcePanelState;
};

/** 渲染桌面和移动端共用的资源分类列表。 */
function ResourcePanelLists({
  activeCategory,
  categories,
  currentProductName,
  desktopCategories,
  downloadAction,
  downloadLinkLabels,
  emptyResources,
  onDownload,
  state,
}: ResourcePanelListsProps) {
  return (
    <>
      <MobileResourceCategoryTabs
        activeCategoryId={activeCategory?.id}
        categories={categories}
        currentProductName={currentProductName}
        onSelect={state.setActiveCategoryId}
      />
      <DesktopResourceCategoryList
        categories={desktopCategories}
        downloadAction={downloadAction}
        downloadLinkLabels={downloadLinkLabels}
        emptyResources={emptyResources}
        onDownload={onDownload}
        scrollerRef={state.resourceScrollerRef}
      />
      <MobileResourceCategoryContent
        category={activeCategory}
        downloadAction={downloadAction}
        downloadLinkLabels={downloadLinkLabels}
        emptyResources={emptyResources}
        onDownload={onDownload}
      />
    </>
  );
}

function getActiveResourceCategory(
  categories: DownloadResourceCategory[],
  categoryId: number | null,
) {
  return categories.find((category) => category.id === categoryId) ?? categories[0];
}

function getDesktopResourceCategories(
  categories: DownloadResourceCategory[],
  categoryId: number | null,
) {
  if (categoryId === null) return categories;
  return categories.filter((category) => category.id === categoryId);
}

/** 管理资源 Tab、右侧滚动位置和下载弹窗状态。 */
function useDownloadResourcePanelState(
  categories: DownloadResourceCategory[],
  currentProductId?: number,
) {
  const firstCategoryId = categories[0]?.id ?? null;
  const resourceScrollerRef = useRef<HTMLDivElement>(null);
  const [mobileSelection, setMobileSelection] =
    useState<MobileCategorySelection>({
      categoryId: firstCategoryId,
      productId: currentProductId,
    });
  const [downloadDialog, setDownloadDialog] =
    useState<DownloadDialogState | null>(null);
  const [desktopSelection, setDesktopSelection] =
    useState<DesktopCategorySelection>({
      categoryId: null,
      productId: currentProductId,
    });
  const activeCategoryId =
    mobileSelection.productId === currentProductId
      ? mobileSelection.categoryId
      : firstCategoryId;
  const activeDesktopCategoryId =
    desktopSelection.productId === currentProductId
      ? desktopSelection.categoryId
      : null;

  useEffect(() => {
    resourceScrollerRef.current?.scrollTo({ top: 0 });
  }, [currentProductId, firstCategoryId]);

  return {
    activeCategoryId,
    activeDesktopCategoryId,
    downloadDialog,
    resourceScrollerRef,
    setActiveCategoryId: (categoryId: number) => {
      setMobileSelection({ categoryId, productId: currentProductId });
    },
    setActiveDesktopCategoryId: (categoryId: number | null) => {
      setDesktopSelection({ categoryId, productId: currentProductId });
    },
    setDownloadDialog,
  };
}

/** 渲染资源区产品标题。 */
function ProductResourceTitle({ name }: { name: string }) {
  return (
    <div className="flex min-h-[42px] items-center justify-between gap-6">
      <h1 className="min-w-0 break-words pl-px text-[24px] font-bold leading-[29px] text-black lg:truncate lg:pl-0">
        {name}
      </h1>
    </div>
  );
}

type ResourcePanelStickyHeaderProps = {
  activeCategoryId: number | null;
  allLabel: string;
  categories: DownloadResourceCategory[];
  currentProductName: string;
  onSelect: (categoryId: number | null) => void;
};

/** 将产品标题和桌面资源分类 Tab 组合为滚动时持续可见的吸顶头。 */
function ResourcePanelStickyHeader({
  activeCategoryId,
  allLabel,
  categories,
  currentProductName,
  onSelect,
}: ResourcePanelStickyHeaderProps) {
  return (
    <div className={styles.downloadResourceStickyHeader}>
      <ProductResourceTitle name={currentProductName} />
      <DesktopResourceCategoryTabs
        activeCategoryId={activeCategoryId}
        allLabel={allLabel}
        categories={categories}
        currentProductName={currentProductName}
        onSelect={onSelect}
      />
    </div>
  );
}

/** 渲染产品不存在时仍保持稳定高度的右侧面板。 */
function MissingProductPanel({ message }: { message: string }) {
  return (
    <section
      className={cn(
        "relative left-px min-w-0 scroll-mt-[104px] lg:left-0 lg:scroll-mt-20",
        styles.downloadPageResourcePanel,
      )}
      id={DOWNLOAD_RESOURCE_PANEL_ID}
    >
      <ProductResourceTitle name={message} />
      <div className="lg:hidden">
        <EmptyState
          icon={<Package className="size-6" />}
          message={message}
        />
      </div>
      <div className={styles.downloadPageResourceScroller}>
        <EmptyState
          icon={<Package className="size-6" />}
          message={message}
        />
      </div>
    </section>
  );
}

type MobileResourceCategoryTabsProps = {
  activeCategoryId?: number;
  categories: DownloadResourceCategory[];
  currentProductName: string;
  onSelect: (categoryId: number) => void;
};

/** 渲染移动端横向资源分类 Tab。 */
function MobileResourceCategoryTabs({
  activeCategoryId,
  categories,
  currentProductName,
  onSelect,
}: MobileResourceCategoryTabsProps) {
  if (categories.length === 0) return null;

  return (
    <div className="mt-4 w-full max-w-full touch-pan-x snap-x snap-proximity overflow-x-auto overscroll-x-contain [scrollbar-width:none] lg:hidden [&::-webkit-scrollbar]:hidden">
      <div
        aria-label={currentProductName}
        className="ml-px flex w-max min-w-full items-start gap-x-10 border-b border-[#d9d9d9] whitespace-nowrap text-base leading-[19px]"
        role="tablist"
      >
        {categories.map((category) => (
          <MobileResourceCategoryTab
            active={activeCategoryId === category.id}
            category={category}
            key={category.id}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

/** 渲染单个移动端资源分类 Tab。 */
function MobileResourceCategoryTab({
  active,
  category,
  onSelect,
}: {
  active: boolean;
  category: DownloadResourceCategory;
  onSelect: (categoryId: number) => void;
}) {
  return (
    <button
      aria-selected={active}
      className={cn(
        "relative shrink-0 snap-start pb-6 text-left transition hover:text-black",
        active ? "text-black" : "text-[#666666]",
      )}
      onClick={(event) => {
        onSelect(category.id);
        event.currentTarget.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }}
      role="tab"
      type="button"
    >
      {category.name}
      {active ? (
        <span className="absolute bottom-[-1px] left-0 h-[2.5px] w-full bg-black" />
      ) : null}
    </button>
  );
}

type DesktopResourceCategoryTabsProps = {
  activeCategoryId: number | null;
  allLabel: string;
  categories: DownloadResourceCategory[];
  currentProductName: string;
  onSelect: (categoryId: number | null) => void;
};

/** 渲染桌面端资源分类 Tab，并支持全部或单分类筛选。 */
function DesktopResourceCategoryTabs({
  activeCategoryId,
  allLabel,
  categories,
  currentProductName,
  onSelect,
}: DesktopResourceCategoryTabsProps) {
  return (
    <div
      aria-label={currentProductName}
      className={styles.downloadResourceDesktopTabs}
      role="tablist"
    >
      <DesktopResourceCategoryTab
        active={activeCategoryId === null}
        label={allLabel}
        onSelect={() => onSelect(null)}
      />
      {categories.map((category) => (
        <DesktopResourceCategoryTab
          active={activeCategoryId === category.id}
          key={category.id}
          label={category.name}
          onSelect={() => onSelect(category.id)}
        />
      ))}
    </div>
  );
}

/** 渲染桌面端单个资源分类 Tab。 */
function DesktopResourceCategoryTab({
  active,
  label,
  onSelect,
}: {
  active: boolean;
  label: string;
  onSelect: () => void;
}) {
  return (
    <button
      aria-selected={active}
      className={cn(
        styles.downloadResourceDesktopTab,
        active && styles.downloadResourceDesktopTabActive,
      )}
      onClick={() => {
        onSelect();
        scrollDownloadResourcePanelToTop();
      }}
      role="tab"
      type="button"
    >
      {label}
    </button>
  );
}

type DesktopResourceCategoryListProps = {
  categories: DownloadResourceCategory[];
  downloadAction: string;
  downloadLinkLabels: Record<string, string>;
  emptyResources: string;
  onDownload: (post: DownloadResourcePost, links: DownloadLink[]) => void;
  scrollerRef: RefObject<HTMLDivElement | null>;
};

/** 渲染桌面端当前筛选范围内的资源分类。 */
function DesktopResourceCategoryList({
  categories,
  downloadAction,
  downloadLinkLabels,
  emptyResources,
  onDownload,
  scrollerRef,
}: DesktopResourceCategoryListProps) {
  return (
    <div className={styles.downloadPageResourceScroller} ref={scrollerRef}>
      {categories.length > 0 ? (
        <div className={styles.downloadResourceCategoryList}>
          {categories.map((category) => (
            <ResourceCategorySection
              category={category}
              downloadAction={downloadAction}
              downloadLinkLabels={downloadLinkLabels}
              emptyResources={emptyResources}
              key={category.id}
              onDownload={onDownload}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<FileDown className="size-6" />}
          message={emptyResources}
        />
      )}
    </div>
  );
}

/** 渲染桌面端单个资源分类及其单列卡片列表。 */
function ResourceCategorySection({
  category,
  downloadAction,
  downloadLinkLabels,
  emptyResources,
  onDownload,
}: Omit<DesktopResourceCategoryListProps, "categories" | "scrollerRef"> & {
  category: DownloadResourceCategory;
}) {
  return (
    <section>
      <div className={styles.downloadResourceCategoryHeader}>
        <h2 className={styles.downloadResourceCategoryTitle}>
          {category.name}
        </h2>
      </div>
      {category.posts.length > 0 ? (
        <div className={styles.downloadResourcePostGrid}>
          {category.posts.map((post) => (
            <ResourcePostCard
              downloadAction={downloadAction}
              downloadLinkLabels={downloadLinkLabels}
              key={post.id}
              onDownload={onDownload}
              post={post}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          compact
          icon={<FileDown className="size-5" />}
          message={emptyResources}
        />
      )}
    </section>
  );
}

/** 渲染移动端当前激活分类的资源内容。 */
function MobileResourceCategoryContent({
  category,
  downloadAction,
  downloadLinkLabels,
  emptyResources,
  onDownload,
}: Omit<ResourcePostListProps, "posts"> & {
  category?: DownloadResourceCategory;
}) {
  return (
    <div className="lg:hidden">
      {category ? (
        <ResourcePostList
          downloadAction={downloadAction}
          downloadLinkLabels={downloadLinkLabels}
          emptyResources={emptyResources}
          onDownload={onDownload}
          posts={category.posts}
        />
      ) : (
        <EmptyState
          icon={<FileDown className="size-6" />}
          message={emptyResources}
        />
      )}
    </div>
  );
}

type ResourcePostListProps = {
  downloadAction: string;
  downloadLinkLabels: Record<string, string>;
  emptyResources: string;
  onDownload: (post: DownloadResourcePost, links: DownloadLink[]) => void;
  posts: DownloadResourcePost[];
};

/**
 * 渲染当前下载分类下的资源列表。
 * @param props 组件参数。
 * @param props.downloadAction 下载按钮文案。
 * @param props.downloadLinkLabels 下载地址 key 的多语言显示文案。
 * @param props.emptyResources 空资源文案。
 * @param props.onDownload 点击下载按钮后的回调。
 * @param props.posts 当前分类资源列表。
 */
function ResourcePostList({
  downloadAction,
  downloadLinkLabels,
  emptyResources,
  onDownload,
  posts,
}: ResourcePostListProps) {
  if (posts.length === 0) {
    return (
      <EmptyState
        icon={<FileDown className="size-6" />}
        message={emptyResources}
      />
    );
  }

  return (
    <div className="grid gap-4 pt-5">
      {posts.map((post) => (
        <ResourcePostCard
          downloadAction={downloadAction}
          downloadLinkLabels={downloadLinkLabels}
          key={post.id}
          onDownload={onDownload}
          post={post}
        />
      ))}
    </div>
  );
}
